// Zod-схемы модуля admin (Phase 6 — PHASE-6-ADMIN.md §4).
//
// Namespace-структура: admin.dashboard / users / materials / quotas /
// tariffs / aiUsage / auditLogs / templates / legalDocs / workflows /
// billing / featureFlags / killswitch / health / impersonation.
import { z } from "zod";
import {
	accountEmailSchema,
	countryCodeSchema,
	isoDateSchema,
	isoDateTimeSchema,
	localeSchema,
	uuidSchema,
} from "../_shared/common.js";
import { cursorPaginationInput, cursorPaginationOutput } from "../_shared/pagination.js";
// Цена курса описана в контракте `courseTemplates` — админская мутация берёт
// ЕГО схему, а не свою копию: разъехавшиеся определения валюты и модели оплаты
// означают, что админка сохранит то, чего витрина не умеет показать.
import {
	COURSE_PRICING_CURRENCY,
	coursePricingKindSchema,
} from "../course-templates/catalog.schemas.js";

// ── Общие ───────────────────────────────────────────────────────────────

export const USER_ROLES = ["user", "moderator", "admin", "superadmin"] as const;
export const userRoleSchema = z.enum(USER_ROLES);
export const subscriptionTierSchema = z.enum(["free", "plus", "pro"]);
export const userStatusSchema = z.enum(["active", "banned", "suspended", "pending_deletion"]);
export const okSchema = z.object({ ok: z.literal(true) });

// ── Dashboard ───────────────────────────────────────────────────────────

export const dashboardInput = z
	.object({
		dateRange: z.object({ from: isoDateTimeSchema, to: isoDateTimeSchema }).optional(),
	})
	.default({});

export const dashboardOutput = z.object({
	/** AI spend за последние 24ч (USD cents). */
	aiSpendTodayMinor: z.number().int().nonnegative(),
	/** AI spend за последние 30 дней (USD cents). Legacy alias: aiSpendMinor. */
	aiSpendMinor: z.number().int().nonnegative(),
	mtdRevenueMinor: z.number().int().nonnegative(),
	activeUsers: z.object({
		dau: z.number().int().nonnegative(),
		wau: z.number().int().nonnegative(),
		mau: z.number().int().nonnegative(),
	}),
	newSignups: z.object({
		d1: z.number().int().nonnegative(),
		d7: z.number().int().nonnegative(),
		d30: z.number().int().nonnegative(),
	}),
	topMaterials: z.array(z.object({ id: uuidSchema, title: z.string(), views: z.number().int() })),
	materialsByType: z.array(
		z.object({
			type: z.string(),
			count: z.number().int().nonnegative(),
		}),
	),
	topUsersBySpend: z.array(
		z.object({
			userId: uuidSchema,
			email: z.string(),
			spendMinor: z.number().int(),
		}),
	),
	queueDepth: z.number().int().nonnegative(),
	dlqCount: z.number().int().nonnegative(),
	pendingModerationReports: z.number().int().nonnegative(),
	/** Всего материалов (не удалённых) — для KPI «Материалов всего». */
	totalMaterials: z.number().int().nonnegative(),
	/** Активных платных подписок (status='active', tier != 'free') — KPI «Активных подписок». */
	activeSubscriptions: z.number().int().nonnegative(),
});

// ── Users ───────────────────────────────────────────────────────────────

export const adminUserRowSchema = z.object({
	id: uuidSchema,
	// Нестандартный идентификатор входа показываем как есть, не скрывая реального
	// пользователя от админа. Почему не `.email()` — `accountEmailSchema` (OXEM-21).
	email: accountEmailSchema,
	fullName: z.string().nullable(),
	/** Opaque DiceBear seed (SHA-256 email); для паритета с Hero / web UserAvatar. */
	avatarSeed: z.string().regex(/^[0-9a-f]{64}$/),
	country: z.string().nullable(),
	role: userRoleSchema,
	subscriptionTier: subscriptionTierSchema,
	status: userStatusSchema,
	createdAt: isoDateTimeSchema,
	lastSeenAt: isoDateTimeSchema.nullable(),
	deletionRequestedAt: isoDateTimeSchema.nullable(),
});

export const listUsersInput = cursorPaginationInput({
	defaultLimit: 50,
	maxLimit: 100,
}).extend({
	filters: z
		.object({
			tier: subscriptionTierSchema.optional(),
			status: userStatusSchema.optional(),
			country: countryCodeSchema.optional(),
			search: z.string().max(200).optional(),
		})
		.default({}),
});
export const listUsersOutput = cursorPaginationOutput(adminUserRowSchema);

export const getUserInput = z.object({ userId: uuidSchema });
export const getUserOutput = z.object({
	user: adminUserRowSchema,
	subscription: z
		.object({
			tier: subscriptionTierSchema,
			status: z.string(),
			currentPeriodEnd: isoDateTimeSchema.nullable(),
		})
		.nullable(),
	quotaOverride: z
		.object({
			customLimits: z.record(z.string(), z.number()),
			reason: z.string().nullable(),
			expiresAt: isoDateTimeSchema.nullable(),
		})
		.nullable(),
	stats: z.object({
		materialsCount: z.number().int(),
		aiSpendMinor: z.number().int(),
	}),
	recentActivity: z.array(
		z.object({
			action: z.string(),
			at: isoDateTimeSchema,
			targetType: z.string().nullable(),
		}),
	),
});

export const banUserInput = z.object({
	userId: uuidSchema,
	reason: z.string().min(3).max(500),
	durationDays: z.number().int().min(1).optional(),
});
export const unbanUserInput = z.object({ userId: uuidSchema });
export const suspendUserInput = z.object({
	userId: uuidSchema,
	reason: z.string().min(3).max(500),
	durationDays: z.number().int().min(1),
});
export const deleteAccountInput = z.object({
	userId: uuidSchema,
	reason: z.string().min(10).max(500),
	skipGracePeriod: z.boolean().default(false),
});
export const updateRoleInput = z.object({
	userId: uuidSchema,
	role: userRoleSchema,
});
export const searchUsersInput = z.object({ query: z.string().min(1).max(200) });
export const searchUsersOutput = z.array(
	z.object({
		id: uuidSchema,
		email: z.string(),
		fullName: z.string().nullable(),
		role: userRoleSchema,
	}),
);

// ── Materials ───────────────────────────────────────────────────────────

export const adminMaterialRowSchema = z.object({
	id: uuidSchema,
	title: z.string(),
	ownerUserId: uuidSchema,
	type: z.string(),
	status: z.string(),
	deletedAt: isoDateTimeSchema.nullable(),
	createdAt: isoDateTimeSchema,
});
export const listMaterialsInput = cursorPaginationInput({
	defaultLimit: 50,
	maxLimit: 100,
}).extend({
	filters: z
		.object({
			ownerUserId: uuidSchema.optional(),
			status: z.string().optional(),
			includeSoftDeleted: z.boolean().optional(),
		})
		.default({}),
});
export const listMaterialsOutput = cursorPaginationOutput(adminMaterialRowSchema);
export const getMaterialInput = z.object({ materialId: uuidSchema });
/** Detail row for admin.materials.get — list row + optional source metadata. */
export const adminMaterialDetailSchema = adminMaterialRowSchema.extend({
	/** Materials table has no `description` column — always null today. */
	description: z.string().nullable(),
	sourceUrl: z.string().nullable(),
	mimeType: z.string().nullable(),
	sizeBytes: z.number().int().nonnegative().nullable(),
	processingError: z.string().nullable(),
});
export const getMaterialOutput = z.object({
	material: adminMaterialDetailSchema,
	chunkCount: z.number().int().nonnegative(),
	ownerEmail: z.string().nullable(),
	/** Presigned download URL when storage port is wired into AdminRouterDeps; else null. */
	signedUrl: z.string().nullable(),
	/** First ~2000 chars from material.content or document_chunks. */
	excerpt: z.string().nullable(),
	/** materials.transcription (audio/recording types); else null. */
	transcript: z.string().nullable(),
	childCounts: z.object({
		notes: z.number().int().nonnegative(),
		flashcards: z.number().int().nonnegative(),
		quizzes: z.number().int().nonnegative(),
		recordings: z.number().int().nonnegative(),
		audioSummaries: z.number().int().nonnegative(),
	}),
	/**
	 * Sum of ai_usage_log for this material. Column `material_id` is absent on
	 * ai_usage_log today → always 0 until provenance is denormalized.
	 */
	aiSpendMinor: z.number().int().nonnegative(),
	moderationOpenCount: z.number().int().nonnegative(),
});
export const forceDeleteMaterialInput = z.object({
	materialId: uuidSchema,
	reason: z.string().min(3).max(500),
	cascadeNotes: z.boolean().default(true),
});
export const restoreMaterialInput = z.object({ materialId: uuidSchema });
export const retryProcessingInput = z.object({ materialId: uuidSchema });

// ── User content (admin.users.list*) ────────────────────────────────────
// Thin lists for user detail tabs. materials.list also supports filters.ownerUserId.

export const listUserContentInput = cursorPaginationInput({
	defaultLimit: 50,
	maxLimit: 100,
}).extend({
	userId: uuidSchema,
});

export const adminUserContentRowSchema = z.object({
	id: uuidSchema,
	title: z.string(),
	status: z.string(),
	createdAt: isoDateTimeSchema,
	materialId: uuidSchema.nullable(),
});
export const listUserContentOutput = cursorPaginationOutput(adminUserContentRowSchema);

/** UGC kinds previewable via admin.users.getContent. */
export const adminContentKindSchema = z.enum(["note", "flashcard", "quiz", "recording", "essay"]);

export const getContentInput = z.object({
	userId: uuidSchema,
	kind: adminContentKindSchema,
	id: z.string().min(1),
});

export const getContentOutput = z.object({
	kind: adminContentKindSchema,
	id: z.string(),
	title: z.string(),
	status: z.string(),
	createdAt: isoDateTimeSchema,
	materialId: z.string().nullable(),
	/** Plain text / markdown / transcript excerpt for preview (max ~20k chars). */
	body: z.string().nullable(),
	/** Extra structured preview. */
	meta: z.record(z.string(), z.unknown()).optional(),
	/** For flashcards: up to 20 cards {front, back}. */
	cards: z.array(z.object({ front: z.string(), back: z.string() })).optional(),
	/** For quizzes: up to 30 questions {prompt, type}. */
	questions: z.array(z.object({ prompt: z.string(), type: z.string() })).optional(),
});

/** User courses = study_plans (admin.users.listCourses). */
export const adminUserCourseRowSchema = z.object({
	id: uuidSchema,
	title: z.string(),
	status: z.string(),
	progressPct: z.number().int().nonnegative(),
	completedSubsections: z.number().int().nonnegative(),
	totalSubsections: z.number().int().nonnegative(),
	createdAt: isoDateTimeSchema,
	updatedAt: isoDateTimeSchema,
});
export const listCoursesOutput = cursorPaginationOutput(adminUserCourseRowSchema);

export const listUserSessionsInput = z.object({ userId: uuidSchema });
export const listUserSessionsOutput = z.array(
	z.object({
		/** Better Auth session.id is text PK (not necessarily UUID). */
		id: z.string().min(1),
		/** Better Auth `session` has no device column — null unless derived later. */
		device: z.string().nullable(),
		ip: z.string().nullable(),
		userAgent: z.string().nullable(),
		createdAt: isoDateTimeSchema,
		lastSeenAt: isoDateTimeSchema.nullable(),
	}),
);
export const revokeUserSessionsInput = z.object({
	userId: uuidSchema,
	reason: z.string().min(3).max(500),
});
export const revokeUserSessionsOutput = z.object({
	ok: z.literal(true),
	revokedCount: z.number().int().nonnegative(),
});
export const revokeUserSessionInput = z.object({
	userId: uuidSchema,
	sessionId: z.string().min(1).max(200),
	reason: z.string().min(3).max(500),
});
export const revokeUserSessionOutput = z.object({
	ok: z.literal(true),
});

// ── Quotas ──────────────────────────────────────────────────────────────

export const QUOTA_TYPES = [
	"ai_generations_daily",
	"ai_generations_monthly",
	"recordings_minutes_daily",
	"storage_gb",
	"materials_count",
	"voice_minutes_daily",
] as const;
export const quotaTypeSchema = z.enum(QUOTA_TYPES);

export const quotaOverrideRowSchema = z.object({
	userId: uuidSchema,
	customLimits: z.record(z.string(), z.number()),
	reason: z.string().nullable(),
	expiresAt: isoDateTimeSchema.nullable(),
	setBy: z.string().nullable(),
	updatedAt: isoDateTimeSchema,
});
export const listOverridesInput = cursorPaginationInput({
	defaultLimit: 50,
	maxLimit: 100,
});
export const listOverridesOutput = cursorPaginationOutput(quotaOverrideRowSchema);
export const setQuotaOverrideInput = z.object({
	userId: uuidSchema,
	quotaType: quotaTypeSchema,
	customLimit: z.number().int().min(0),
	expiresAt: isoDateTimeSchema.optional(),
	reason: z.string().min(3).max(500),
});
export const removeQuotaOverrideInput = z.object({
	userId: uuidSchema,
	quotaType: quotaTypeSchema,
});
export const tierDefaultSchema = z.object({
	tier: subscriptionTierSchema,
	limits: z.record(z.string(), z.number().nullable()),
});
export const tierDefaultsOutput = z.object({
	items: z.array(tierDefaultSchema),
});

// ── Tariffs ─────────────────────────────────────────────────────────────

export const tariffRowSchema = z.object({
	tier: subscriptionTierSchema,
	priceMinor: z.number().int().nonnegative(),
	currency: z.enum(["RUB", "USD", "EUR"]),
	limits: z.record(z.string(), z.number().nullable()),
});
export const listTariffsOutput = z.object({ items: z.array(tariffRowSchema) });
export const updateTariffInput = z.object({
	tier: subscriptionTierSchema,
	priceMinor: z.number().int().min(0).optional(),
	currency: z.enum(["RUB", "USD", "EUR"]).optional(),
	limits: z.record(z.string(), z.number().nullable()).optional(),
	reason: z.string().min(10).max(500),
});

// ── AI usage ────────────────────────────────────────────────────────────

export const aiUsageGroupBySchema = z.enum(["model", "useCase", "user", "day", "promptVersion"]);
export const aiUsageSummaryInput = z.object({
	dateFrom: isoDateSchema,
	dateTo: isoDateSchema,
	groupBy: aiUsageGroupBySchema,
	filters: z
		.object({
			userId: uuidSchema.optional(),
			model: z.string().optional(),
			useCase: z.string().optional(),
			promptVersion: z.string().optional(),
		})
		.default({}),
});
export const aiUsageSummaryRowSchema = z.object({
	groupKey: z.string(),
	callCount: z.number().int().nonnegative(),
	tokensIn: z.number().int().nonnegative(),
	tokensOut: z.number().int().nonnegative(),
	costMinor: z.number().int().nonnegative(),
});
export const aiUsageSummaryOutput = z.object({
	rows: z.array(aiUsageSummaryRowSchema),
	totalCostMinor: z.number().int().nonnegative(),
	totalCalls: z.number().int().nonnegative(),
});
export const aiUsageDetailInput = cursorPaginationInput({
	defaultLimit: 50,
	maxLimit: 200,
}).extend({
	filters: z.object({
		dateFrom: isoDateSchema,
		dateTo: isoDateSchema,
		userId: uuidSchema.optional(),
		model: z.string().optional(),
		useCase: z.string().optional(),
		promptVersion: z.string().optional(),
		success: z.boolean().optional(),
	}),
});
export const aiUsageDetailRowSchema = z.object({
	id: uuidSchema,
	userId: uuidSchema.nullable(),
	useCase: z.string(),
	model: z.string(),
	promptVersion: z.string().nullable(),
	costMinor: z.number().int(),
	tokensIn: z.number().int(),
	tokensOut: z.number().int(),
	success: z.boolean(),
	createdAt: isoDateTimeSchema,
});
export const aiUsageDetailOutput = cursorPaginationOutput(aiUsageDetailRowSchema);
// ── Расходы у внешних (федеративных) сервисов — Ш6 ──────────────────────
//
// Отдельный срез, а не строки в `aiUsageSummary`: проксируемая генерация идёт
// мимо `aiPort` и живёт в своей таблице `partner_ai_usage`. Смешивать её с
// собственными расходами в одной выборке нельзя — у них разные единицы доверия
// (наши цифры мы считаем сами, партнёрские сверяем с его отчётом).

export const partnerUsageSummaryInput = z.object({
	dateFrom: isoDateSchema,
	dateTo: isoDateSchema,
	serviceId: z.string().optional(),
});
export const partnerUsageSummaryRowSchema = z.object({
	serviceId: z.string(),
	operation: z.string(),
	callCount: z.number().int().nonnegative(),
	tokensIn: z.number().int().nonnegative(),
	tokensOut: z.number().int().nonnegative(),
	seconds: z.number().nonnegative(),
	costMinor: z.number().int().nonnegative(),
});
/** Последняя сверка по сервису — «сходятся ли наши цифры с их». */
export const partnerReconciliationRowSchema = z.object({
	serviceId: z.string(),
	operation: z.string(),
	periodFrom: isoDateTimeSchema,
	periodTo: isoDateTimeSchema,
	partnerCalls: z.number().int().nonnegative(),
	localCalls: z.number().int().nonnegative(),
	driftPct: z.number(),
	status: z.enum(["ok", "drift", "partner_unavailable"]),
});
export const partnerUsageSummaryOutput = z.object({
	rows: z.array(partnerUsageSummaryRowSchema),
	reconciliations: z.array(partnerReconciliationRowSchema),
	totalCostMinor: z.number().int().nonnegative(),
	totalCalls: z.number().int().nonnegative(),
	/** Порог, выше которого расхождение считается алертом (проценты). */
	driftAlertPct: z.number().int().positive(),
});

export const topSpendersInput = z.object({
	dateFrom: isoDateSchema,
	dateTo: isoDateSchema,
	limit: z.number().int().min(1).max(100).default(10),
});
export const topSpendersOutput = z.array(
	z.object({
		userId: uuidSchema,
		email: z.string().nullable(),
		spendMinor: z.number().int(),
	}),
);
export const costBudgetOutput = z.object({
	monthlyBudgetMinor: z.number().int().nonnegative().nullable(),
	currentSpendMinor: z.number().int().nonnegative(),
	currency: z.literal("USD"),
	alertThresholds: z.array(z.number()),
});
export const setCostBudgetInput = z.object({
	monthlyBudgetMinor: z.number().int().min(0),
	currency: z.literal("USD"),
	alertThresholds: z.array(z.number()).default([50, 80, 100]),
});
export const promptVersionsListInput = z.object({ useCase: z.string().optional() }).default({});
export const promptVersionsListOutput = z.array(
	z.object({ promptVersion: z.string(), useCase: z.string() }),
);

// ── Audit logs ──────────────────────────────────────────────────────────

export const auditLogRowSchema = z.object({
	id: uuidSchema,
	source: z.enum(["system", "admin"]),
	actorUserId: z.string().nullable(),
	action: z.string(),
	targetType: z.string().nullable(),
	targetId: z.string().nullable(),
	payload: z.unknown().nullable(),
	createdAt: isoDateTimeSchema,
});
export const listAuditLogsInput = cursorPaginationInput({
	defaultLimit: 50,
	maxLimit: 200,
}).extend({
	filters: z
		.object({
			actorUserId: uuidSchema.optional(),
			action: z.array(z.string()).optional(),
			targetType: z.string().optional(),
			targetId: z.string().optional(),
			dateFrom: isoDateTimeSchema.optional(),
			dateTo: isoDateTimeSchema.optional(),
			// default 'all' резолвится в handler'е (Zod v4: inner .default
			// делает поле required в parent .default({})).
			source: z.enum(["system", "admin", "all"]).optional(),
		})
		.default({}),
});
export const listAuditLogsOutput = cursorPaginationOutput(auditLogRowSchema);
export const getAuditLogInput = z.object({
	id: uuidSchema,
	source: z.enum(["system", "admin"]),
});
export const exportAuditCsvInput = z.object({
	filters: z
		.object({
			actorUserId: uuidSchema.optional(),
			action: z.array(z.string()).optional(),
			source: z.enum(["system", "admin", "all"]).optional(),
		})
		.default({}),
});
export const exportAuditCsvOutput = z.object({
	rowCount: z.number().int().nonnegative(),
	csv: z.string(),
});

// ── Templates ───────────────────────────────────────────────────────────

export const templateRowSchema = z.object({
	id: uuidSchema,
	slug: z.string(),
	/**
	 * `null` — у программы нет страны (общий курс, миграция 0123). Заглушку
	 * вместо `null` подставлять нельзя: это же значение уходит в фасет
	 * публичного каталога и создаёт там страну-призрак.
	 */
	country: z.string().nullable(),
	educationSubsystem: z.string().nullable(),
	titleLocalized: z.record(z.string(), z.string()),
	version: z.number().int(),
	isPublished: z.boolean(),
	locale: z.string(),
	createdAt: isoDateTimeSchema,
});
export const listTemplatesAdminInput = z
	.object({
		country: z.string().optional(),
		educationSubsystem: z.string().optional(),
		locale: localeSchema.optional(),
		isPublished: z.boolean().optional(),
	})
	.default({});
export const listTemplatesAdminOutput = z.object({
	items: z.array(templateRowSchema),
});
export const getTemplateAdminInput = z.object({ templateId: uuidSchema });
export const getTemplateAdminOutput = z.object({
	template: templateRowSchema,
	sections: z.unknown(),
});
export const publishTemplateInput = z.object({
	templateId: uuidSchema,
	changelog: z.string().min(10).max(2000),
});
export const unpublishTemplateInput = z.object({ templateId: uuidSchema });
export const deleteTemplateInput = z.object({ templateId: uuidSchema });

// ── Course templates: цена курса ────────────────────────────────────────
//
// Единственный путь назначить цену курсу (§3.6 плана публичных страниц). У
// автора такой ручки нет: `courseTemplatesPublishInput` ценовых полей не
// содержит и сервер игнорирует их, даже если клиент пришлёт. Причина не в
// недоверии к автору, а в следе: цена — финансовое решение, оно обязано лежать
// в админском аудите (`insertAdminAuditLog`) с именем того, кто его принял.

/**
 * Установить цену курса-шаблона.
 *
 * `currency` — литерал USD (единственная валюта курсов, см.
 * `coursePricingSchema`). Поле присутствует явно, а не подразумевается: цена
 * без валюты в запросе — это приглашение однажды прочитать «1990» как рубли.
 *
 * Refine здесь обязателен (в отличие от схемы выдачи): значение приходит
 * снаружи, и пара `kind`/`priceMinor` должна быть согласована ДО похода в базу
 * — иначе единственной защитой останется CHECK миграции 0119, и админ вместо
 * внятной ошибки формы получит 500 от Postgres.
 */
export const setCoursePricingInput = z
	.object({
		templateId: uuidSchema,
		kind: coursePricingKindSchema,
		priceMinor: z.number().int().nullable(),
		currency: z.literal(COURSE_PRICING_CURRENCY),
	})
	.refine((v) => (v.kind === "paid" ? v.priceMinor !== null && v.priceMinor > 0 : true), {
		message: "Для платного курса нужна цена больше нуля",
		path: ["priceMinor"],
	})
	.refine((v) => (v.kind === "free" ? v.priceMinor === null : true), {
		// Не «обнуляем молча»: бесплатный курс с сохранённой ценой — это мина,
		// которая выстрелит при следующем переключении в `paid` старым числом.
		message: "У бесплатного курса цены быть не должно",
		path: ["priceMinor"],
	});

// ── Legal docs ──────────────────────────────────────────────────────────

export const LEGAL_DOC_TYPES = [
	"privacy",
	"terms",
	"ai_disclaimer",
	"cookie",
	"kid_consent",
	// Передача учебного контента внешнему сервису (Ш6 модульной платформы):
	// версионируемая запись согласия, отзыв = unlink + read_only.
	"partner_data_sharing",
] as const;
export const legalDocTypeSchema = z.enum(LEGAL_DOC_TYPES);
export const legalDocRowSchema = z.object({
	// legal_documents.id — колонка типа TEXT с человекочитаемыми seed-id
	// (напр. "01900000-…-legal0000privacy-ru"), а не UUID; uuid-формат ронял
	// весь output-ответ (OXEM-22). Новые документы (publish) генерят uuidv7 и
	// тоже проходят z.string().
	id: z.string(),
	type: legalDocTypeSchema,
	locale: z.string(),
	version: z.string(),
	title: z.string().nullable(),
	isCurrent: z.boolean(),
	effectiveAt: isoDateTimeSchema,
	createdAt: isoDateTimeSchema,
});
export const listLegalDocsInput = z
	.object({
		type: legalDocTypeSchema.optional(),
		locale: localeSchema.optional(),
	})
	.default({});
export const listLegalDocsOutput = z.object({
	items: z.array(legalDocRowSchema),
});
export const legalHistoryInput = z.object({
	type: legalDocTypeSchema,
	locale: localeSchema,
});
export const legalHistoryOutput = z.object({
	items: z.array(legalDocRowSchema),
});
export const publishLegalInput = z.object({
	type: legalDocTypeSchema,
	locale: z.string().length(2),
	content: z.string().min(50),
	title: z.string().min(1),
	changelog: z.string().min(10),
	semverBump: z.enum(["major", "minor", "patch"]).default("minor"),
	effectiveAt: isoDateTimeSchema.optional(),
});
export const rollbackLegalInput = z.object({
	type: legalDocTypeSchema,
	locale: localeSchema,
	targetVersion: z.string(),
	reason: z.string().min(20),
});

// ── Workflows / DLQ ─────────────────────────────────────────────────────

export const dlqRowSchema = z.object({
	id: uuidSchema,
	runId: z.string(),
	functionName: z.string(),
	eventName: z.string().nullable(),
	userId: z.string().nullable(),
	errorMessage: z.string().nullable(),
	errorCode: z.string().nullable(),
	attempts: z.number().int(),
	replayedAt: isoDateTimeSchema.nullable(),
	resolvedAt: isoDateTimeSchema.nullable(),
	lastFailedAt: isoDateTimeSchema,
	createdAt: isoDateTimeSchema,
});
export const listDlqInput = cursorPaginationInput({
	defaultLimit: 50,
	maxLimit: 200,
}).extend({
	filters: z
		.object({
			functionName: z.string().optional(),
			userId: uuidSchema.optional(),
			errorCode: z.string().optional(),
			includeResolved: z.boolean().optional(),
		})
		.default({}),
});
export const listDlqOutput = cursorPaginationOutput(dlqRowSchema);
export const getDlqInput = z.object({ recordId: uuidSchema });
export const getDlqOutput = z.object({
	record: dlqRowSchema,
	payload: z.unknown().nullable(),
});
export const replayDlqInput = z.object({
	recordId: uuidSchema,
	overrideIdempotencyKey: z.boolean().default(false),
});
export const replayDlqOutput = z.object({
	ok: z.literal(true),
	newIdempotencyKey: z.string(),
	eventName: z.string(),
});
export const markResolvedInput = z.object({
	recordId: uuidSchema,
	notes: z.string().max(2000).optional(),
});
export const bulkReplayInput = z.object({
	recordIds: z.array(uuidSchema).max(100),
});
export const bulkReplayOutput = z.object({
	results: z.array(
		z.object({
			recordId: uuidSchema,
			ok: z.boolean(),
			error: z.string().optional(),
		}),
	),
	totalRequested: z.number().int(),
	succeeded: z.number().int(),
});

// ── Billing ─────────────────────────────────────────────────────────────

export const billingEventRowSchema = z.object({
	id: uuidSchema,
	// nullable: при account deletion userId→NULL, anonymizedUserId=SHA-256(userId).
	// Событие сохраняется для финансового аудита (ota-backups §6.5), но больше
	// не привязано к удалённому пользователю.
	userId: z.string().nullable(),
	anonymizedUserId: z.string().nullable(),
	provider: z.string(),
	eventType: z.string(),
	createdAt: isoDateTimeSchema,
});
export const listBillingEventsInput = cursorPaginationInput({
	defaultLimit: 50,
	maxLimit: 100,
}).extend({
	filters: z
		.object({
			provider: z.string().optional(),
			eventType: z.string().optional(),
			userId: uuidSchema.optional(),
		})
		.default({}),
});
export const listBillingEventsOutput = cursorPaginationOutput(billingEventRowSchema);
export const refundInput = z.object({
	paymentId: uuidSchema,
	amountMinor: z.number().int().min(0).optional(),
	reason: z.string().min(10).max(500),
});

// ── Feature flags ───────────────────────────────────────────────────────

export const featureFlagRowSchema = z.object({
	flagKey: z.string(),
	overrideEnabled: z.boolean(),
	overrideValue: z.unknown().nullable(),
	reason: z.string().nullable(),
	expiresAt: isoDateTimeSchema.nullable(),
	setAt: isoDateTimeSchema,
});
export const listFeatureFlagsOutput = z.object({
	items: z.array(featureFlagRowSchema),
});
export const setFeatureFlagInput = z.object({
	flagKey: z.string().min(1).max(100),
	enabled: z.boolean(),
	value: z.unknown().optional(),
	reason: z.string().min(3).max(500),
	expiresAt: isoDateTimeSchema.optional(),
});

// ── Killswitch ──────────────────────────────────────────────────────────

export const KILLSWITCH_OPS = [
	"ai-generation",
	"ai-tutor-chat",
	"ai-tutor-voice",
	"ai-recordings",
	"ai-audio-recap",
	"ai-essay-grading",
	"ai-visual",
	"ai-glossary",
	"ai-rag",
	"ai-embeddings",
	// Discover sources (план §III.2 W1.2): единственный поток, который тратит
	// платные поисковые запросы и ходит на произвольные внешние сайты —
	// гасится отдельно от остальной генерации.
	"discovery-search",
	// Платный внешний web-search (Exa WebSearchPort) — tutor tool + discovery fan-out.
	"web-search",
	// Проксируемая генерация внешнего модуля `characters` (Ш7). Это КЛАСС
	// AI-операции, а не продуктовый тумблер (продуктовое включение — `ModuleRollout`):
	// «остановить весь AI» обязано гасить и чужую генерацию, за которую платим мы.
	"ai-characters-proxy",
	"ai-all",
	"uploads",
	"billing-checkout",
	// Приём оплаты ПЛАТНЫХ КУРСОВ (план публичных страниц §6.2). Отдельный
	// тумблер от `billing-checkout`: подписка и разовая покупка курса ходят к
	// разным провайдерским потокам, и гасить продажу курсов из-за проблемы с
	// подписками (или наоборот) — терять деньги на здоровом канале.
	// Пока выключен, `enroll` платного курса отвечает «оплата недоступна»
	// (`PAYMENT_UNAVAILABLE`), а не «требуется оплата»: пользователь не виноват
	// и не должен видеть предложение купить то, что купить нельзя.
	"course-payments",
	"sync-write-back",
] as const;
export const killswitchOpSchema = z.enum(KILLSWITCH_OPS);
export const killswitchRowSchema = z.object({
	operationType: killswitchOpSchema,
	enabled: z.boolean(),
	reason: z.string().nullable(),
	toggledBy: z.string().nullable(),
	toggledAt: isoDateTimeSchema,
	userMessageKey: z.string(),
});
export const listKillswitchesOutput = z.object({
	items: z.array(killswitchRowSchema),
});
export const toggleKillswitchInput = z.object({
	operationType: killswitchOpSchema,
	enabled: z.boolean(),
	reason: z.string().min(10).max(500),
	userMessageKey: z.string().optional(),
});

// ── Health ──────────────────────────────────────────────────────────────

export const healthServiceSchema = z.object({
	name: z.string(),
	status: z.enum(["healthy", "degraded", "unhealthy", "unknown"]),
	latencyMs: z.number().int().nullable(),
	message: z.string().nullable(),
});
export const healthCheckInput = z.object({ forceFresh: z.boolean().optional() }).default({});
export const healthCheckOutput = z.object({
	status: z.enum(["healthy", "degraded", "unhealthy"]),
	services: z.array(healthServiceSchema),
	timestamp: isoDateTimeSchema,
	version: z.string().nullable(),
});
export const manualPingInput = z.object({ service: z.string() });
export const manualPingOutput = healthServiceSchema;

// ── Impersonation ───────────────────────────────────────────────────────

export const startImpersonationInput = z.object({
	userId: uuidSchema,
	reason: z.string().min(10).max(500),
	durationMinutes: z.number().int().min(5).max(60).default(30),
});
export const startImpersonationOutput = z.object({
	tempToken: z.string(),
	expiresAt: isoDateTimeSchema,
	sessionId: uuidSchema,
});
export const stopImpersonationInput = z.object({ sessionId: uuidSchema });
export const impersonationSessionRowSchema = z.object({
	id: uuidSchema,
	adminUserId: uuidSchema,
	impersonatedUserId: uuidSchema,
	reason: z.string(),
	startedAt: isoDateTimeSchema,
	expiresAt: isoDateTimeSchema,
	endedAt: isoDateTimeSchema.nullable(),
});
export const listImpersonationsOutput = z.object({
	items: z.array(impersonationSessionRowSchema),
});

// ── Посещаемость публичных страниц ──────────────────────────────────────
//
// Отчёты читают ТОЛЬКО предпосчитанные агрегаты. Запрос по сырью за квартал —
// сотни тысяч строк на каждый показ экрана, и админка положила бы базу, которую
// считает. Единственное исключение оговорено у самих процедур.

/** Сколько суток живёт детализация. Глубже — только суточные агрегаты. */
export const PUBLIC_TRAFFIC_RAW_RETENTION_DAYS = 180;

/**
 * Поверхность.
 *
 * По умолчанию отчёты берут только `web`: заходы из мобильного приложения — не
 * посещаемость сайта, у них по определению нет ни поисковика, ни referrer'а, и
 * подмешав их, мы бы занижали долю поиска по мере роста установок.
 */
export const publicTrafficSurfaceSchema = z.enum(["web", "app", "all"]);

export const publicTrafficRangeInput = z.object({
	dateFrom: isoDateSchema,
	dateTo: isoDateSchema,
	surface: publicTrafficSurfaceSchema.default("web"),
	/** Показывать роботов вместо людей. Разные вопросы — разные наборы строк. */
	bots: z.boolean().default(false),
});

export const publicTrafficSummaryInput = publicTrafficRangeInput.extend({
	granularity: z.enum(["hour", "day"]).default("day"),
});

export const publicTrafficKpiSchema = z.object({
	views: z.number().int().nonnegative(),
	visits: z.number().int().nonnegative(),
	/**
	 * Среднесуточное число уникальных, а НЕ «уникальные за период».
	 *
	 * Хеш посетителя живёт ровно сутки по построению (суточная соль), поэтому
	 * уникальные за неделю недоступны в принципе — это цена отказа от слежки.
	 * Аддитивная метрика периода — визиты.
	 */
	avgDailyVisitors: z.number().int().nonnegative(),
	bounceRatePct: z.number().min(0).max(100),
	pagesPerVisit: z.number().nonnegative(),
	searchViews: z.number().int().nonnegative(),
	searchSharePct: z.number().min(0).max(100),
	botHits: z.number().int().nonnegative(),
});

export const publicTrafficSummaryOutput = z.object({
	current: publicTrafficKpiSchema,
	/** Предыдущий период РАВНОЙ длины — основа для дельт в карточках. */
	previous: publicTrafficKpiSchema,
	series: z.array(
		z.object({
			bucket: isoDateTimeSchema,
			views: z.number().int().nonnegative(),
			visits: z.number().int().nonnegative(),
			botHits: z.number().int().nonnegative(),
		}),
	),
	granularity: z.enum(["hour", "day"]),
	/** Когда агрегаты последний раз досчитывались. `null` — ни разу. */
	computedAt: isoDateTimeSchema.nullable(),
	retentionDays: z.number().int().positive(),
});

export const publicTrafficPagesInput = publicTrafficRangeInput.extend({
	pageType: z.enum(["landing", "static", "catalog", "course", "sitemap"]).optional(),
	limit: z.number().int().min(1).max(100).default(20),
});

export const publicTrafficPagesOutput = z.object({
	rows: z.array(
		z.object({
			pageType: z.string(),
			pageRef: z.string(),
			/** Название курса, если строка про курс. `null` — не нашли, и это честно. */
			title: z.string().nullable(),
			path: z.string(),
			views: z.number().int().nonnegative(),
			entries: z.number().int().nonnegative(),
			searchViews: z.number().int().nonnegative(),
			aiViews: z.number().int().nonnegative(),
			notFoundViews: z.number().int().nonnegative(),
			searchSharePct: z.number().min(0).max(100),
		}),
	),
	totalViews: z.number().int().nonnegative(),
});

export const publicTrafficSourcesOutput = z.object({
	channels: z.array(
		z.object({
			channel: z.string(),
			views: z.number().int().nonnegative(),
			visits: z.number().int().nonnegative(),
			sharePct: z.number().min(0).max(100),
		}),
	),
	referrers: z.array(
		z.object({
			host: z.string(),
			views: z.number().int().nonnegative(),
			visits: z.number().int().nonnegative(),
		}),
	),
	campaigns: z.array(
		z.object({
			source: z.string(),
			medium: z.string(),
			campaign: z.string(),
			views: z.number().int().nonnegative(),
			visits: z.number().int().nonnegative(),
		}),
	),
});

export const publicTrafficGeoOutput = z.object({
	countries: z.array(
		z.object({
			/** ISO 3166-1 alpha-2; пустая строка — страна не определена. */
			country: z.string(),
			views: z.number().int().nonnegative(),
			visits: z.number().int().nonnegative(),
		}),
	),
	locales: z.array(
		z.object({
			/** ISO 639-1; пустая строка — голый (английский) адрес. */
			locale: z.string(),
			views: z.number().int().nonnegative(),
			visits: z.number().int().nonnegative(),
		}),
	),
});

export const publicTrafficCoursesOutput = z.object({
	rows: z.array(
		z.object({
			slug: z.string(),
			title: z.string().nullable(),
			views: z.number().int().nonnegative(),
			searchViews: z.number().int().nonnegative(),
			aiViews: z.number().int().nonnegative(),
			entries: z.number().int().nonnegative(),
			searchSharePct: z.number().min(0).max(100),
			prevSearchViews: z.number().int().nonnegative(),
			/** `null`, если в прошлом периоде было ноль: делить на ноль нечестно. */
			deltaPct: z.number().nullable(),
		}),
	),
});

export const publicTrafficBotsOutput = z.object({
	rows: z.array(
		z.object({
			botName: z.string(),
			botKind: z.string(),
			hits: z.number().int().nonnegative(),
			notFoundHits: z.number().int().nonnegative(),
			notFoundPct: z.number().min(0).max(100),
			distinctPaths: z.number().int().nonnegative(),
			lastSeenAt: isoDateTimeSchema,
		}),
	),
});

export const publicTrafficReaggregateInput = z.object({
	dateFrom: isoDateSchema,
	dateTo: isoDateSchema,
});

// ─────────────────────────────────────────────────────────────────────────────
// admin.acquisition — источники входа и борд (OXEM-400, гайд IEC ID §1.2d, §15)
// ─────────────────────────────────────────────────────────────────────────────
// First / Last touch хранит IEC ID по `sub`. Карточка и агрегаты — его API;
// таблица людей и выгрузка — зеркало карточек у нас (`user_acquisition_touches`)
// плюс наши показатели из очереди отправки.

export const ACQUISITION_SOURCE_TYPES = [
	"utm",
	"organic",
	"referral",
	"direct",
	"internal",
] as const;
export const acquisitionSourceTypeSchema = z.enum(ACQUISITION_SOURCE_TYPES);
export const acquisitionPlatformSchema = z.enum(["web", "ios", "android"]);
export const acquisitionTouchKindSchema = z.enum(["first", "last"]);
/**
 * Поля уровней дерева борда (гайд IEC ID §15.3). Корень — всегда тип
 * источника, глубже уровень выбирает IEC ID: в админке цепочка не хардкодится,
 * список нужен только чтобы принять путь и подписать уровень.
 */
export const ACQUISITION_BOARD_LEVELS = [
	"source_type",
	"utm_source",
	"utm_medium",
	"utm_campaign",
	"utm_content",
	"utm_term",
	"referrer_domain",
	"landing_page",
	"source_service",
	"entry_service",
	"app_platform",
] as const;
export const acquisitionBoardLevelSchema = z.enum(ACQUISITION_BOARD_LEVELS);

/** Шаг пути до раскрываемой строки; `value: null` — «(не задано)». */
export const acquisitionBoardPathStepSchema = z.object({
	field: acquisitionBoardLevelSchema,
	value: z.string().min(1).max(512).nullable(),
});

const optionalText = z.string().trim().min(1).max(200).optional();

/** Фильтры, общие для борда, таблицы и выгрузки. */
export const acquisitionFiltersSchema = z.object({
	touch: acquisitionTouchKindSchema.default("first"),
	sourceTypes: z.array(acquisitionSourceTypeSchema).max(5).optional(),
	utmSource: optionalText,
	utmMedium: optionalText,
	utmCampaign: optionalText,
	referrerDomain: optionalText,
	appPlatform: acquisitionPlatformSchema.optional(),
	/** Дата входа, ISO-8601, `[from, to)`. */
	enteredFrom: z.string().datetime().optional(),
	enteredTo: z.string().datetime().optional(),
	/** Период показателей, ISO-8601, `[from, to)`. Пусто — всё время. */
	occurredFrom: z.string().datetime().optional(),
	occurredTo: z.string().datetime().optional(),
});

export const acquisitionRevenueSchema = z.array(
	z.object({ currency: z.string(), amountMinor: z.number().int() }),
);

export const acquisitionTouchSchema = z.object({
	scope: z.enum(["service", "ecosystem"]),
	kind: acquisitionTouchKindSchema,
	entryService: z.string().nullable(),
	appPlatform: acquisitionPlatformSchema.nullable(),
	sourceType: acquisitionSourceTypeSchema,
	utmSource: z.string().nullable(),
	utmMedium: z.string().nullable(),
	utmCampaign: z.string().nullable(),
	utmTerm: z.string().nullable(),
	utmContent: z.string().nullable(),
	landingPage: z.string().nullable(),
	referrerDomain: z.string().nullable(),
	sourceService: z.string().nullable(),
	touchAt: z.string(),
	enteredAt: z.string(),
});

export const acquisitionUserSourcesInput = z.object({ userId: z.string().min(1) });
export const acquisitionUserSourcesOutput = z.object({
	/** `null` — человек ни разу не входил через единый профиль. */
	sub: z.string().nullable(),
	touches: z.array(acquisitionTouchSchema),
	/**
	 * `live` — прочитано у IEC ID сейчас; `mirror` — IEC ID недоступен, показано
	 * последнее сохранённое (время — `syncedAt`); `none` — источника нет.
	 */
	origin: z.enum(["live", "mirror", "none"]),
	syncedAt: z.string().nullable(),
});

export const ACQUISITION_BOARD_SORTS = [
	"key",
	"users",
	"registrations",
	"purchases",
	"refunds",
	"revenue",
	"generations",
	"appDownloadClicks",
] as const;

export const acquisitionBoardInput = acquisitionFiltersSchema.extend({
	/** Путь от корня до раскрываемой строки; пустой — корень (тип источника). */
	path: z.array(acquisitionBoardPathStepSchema).max(8).default([]),
	sort: z.enum(ACQUISITION_BOARD_SORTS).default("users"),
	order: z.enum(["asc", "desc"]).default("desc"),
	revenueCurrency: z.enum(["USD", "EUR", "KZT", "RUB"]).default("RUB"),
});

const acquisitionBoardTotalsSchema = z.object({
	users: z.number().int(),
	registrations: z.number().int(),
	purchases: z.number().int(),
	refunds: z.number().int(),
	revenue: acquisitionRevenueSchema,
	generations: z.number().int(),
	appDownloadClicks: z.number().int(),
});

export const acquisitionBoardOutput = z.object({
	/** `false` — IEC ID не ответил или ключу не выдан `acquisition.read`. */
	available: z.boolean(),
	/** Поле строк этого уровня; `null` — уровень не получен (`available: false`). */
	level: acquisitionBoardLevelSchema.nullable(),
	path: z.array(acquisitionBoardPathStepSchema),
	rows: z.array(
		acquisitionBoardTotalsSchema.extend({
			key: z.string().nullable(),
			/** Строку можно раскрыть: путь `[...path, { field: level, value: key }]`. */
			hasChildren: z.boolean(),
		}),
	),
	truncated: z.boolean(),
	/** Хвост за лимитом — строка «Прочие (N)»; `values` — N. */
	rest: acquisitionBoardTotalsSchema.extend({ values: z.number().int() }).nullable(),
	/**
	 * Итог узла. Людей в срезе сервиса НЕ складывать из строк: человек с вебом и
	 * приложением из разных источников — в двух строках, а в итоге один раз.
	 */
	total: acquisitionBoardTotalsSchema.nullable(),
});

export const ACQUISITION_USER_SORTS = [
	"enteredAt",
	"touchAt",
	"registrations",
	"purchases",
	"refunds",
	"revenue",
	"generations",
	"appDownloadClicks",
] as const;

export const acquisitionUsersInput = acquisitionFiltersSchema.extend({
	sort: z.enum(ACQUISITION_USER_SORTS).default("enteredAt"),
	order: z.enum(["asc", "desc"]).default("desc"),
	revenueCurrency: z.enum(["USD", "EUR", "KZT", "RUB"]).default("RUB"),
	page: z.number().int().min(0).default(0),
	pageSize: z.number().int().min(1).max(200).default(50),
});

export const acquisitionUserRowSchema = z.object({
	userId: z.string(),
	sub: z.string().nullable(),
	/** Только admin/superadmin: модератор почту не видит (field-level). */
	email: z.string().nullable(),
	platform: acquisitionPlatformSchema,
	sourceType: acquisitionSourceTypeSchema,
	utmSource: z.string().nullable(),
	utmMedium: z.string().nullable(),
	utmCampaign: z.string().nullable(),
	utmTerm: z.string().nullable(),
	utmContent: z.string().nullable(),
	landingPage: z.string().nullable(),
	referrerDomain: z.string().nullable(),
	sourceService: z.string().nullable(),
	touchAt: z.string(),
	enteredAt: z.string(),
	registrations: z.number().int(),
	purchases: z.number().int(),
	refunds: z.number().int(),
	revenue: acquisitionRevenueSchema,
	generations: z.number().int(),
	appDownloadClicks: z.number().int(),
});

export const acquisitionUsersOutput = z.object({
	rows: z.array(acquisitionUserRowSchema),
	total: z.number().int(),
});

export const acquisitionExportInput = acquisitionUsersInput.omit({ page: true, pageSize: true });
export const acquisitionExportOutput = z.object({
	filename: z.string(),
	/** `.xlsx` в base64 — admin API только tRPC (rules/api-layer §8j). */
	base64: z.string(),
	rows: z.number().int(),
	/** Строк больше потолка выгрузки — выгружена первая часть. */
	truncated: z.boolean(),
});
